import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'framer-motion';
import { useAuth } from '@/hooks/useAuth';
import { localizedField } from '@/utils/localizedField';
import { Button } from '@/components/atoms/Button';
import { Spinner } from '@/components/atoms/Spinner';
import { CompletedStoryCard } from './CompletedStoryCard';
import { CompletedStoryDrawer } from './CompletedStoryDrawer';
import { usersAPI, type StoryHistoryItem } from '@/services/api/users.api';
import { usePageTransitionEntrance } from '@/context/PageTransitionEntranceContext';
import {
  storyResourcesEntrance,
  entranceAwareBlockTransition,
} from '@/constants/storyResourcesEntrance';
import styles from './MonthlyStoriesPage.module.scss';

function startOfCurrentMonth(): Date {
  const d = new Date();
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
}

const PAGE_SIZE = 12;
const TITLE_DELAY = 0;
const SUBTITLE_DELAY = 0.14;
const HEADER_BLOCK_DELAY = 0.28;
/** Misma curva y tempo que `ChallengesPage` para que las cards se animen
 * según entran en el viewport (no de golpe al renderizar la página). */
const EASE: [number, number, number, number] = [0.42, 0, 0.58, 1];
const CARD_INITIAL_BASE_DELAY = 0.5;
const CARD_LOAD_MORE_BASE_DELAY = 0;
const CARD_INDEX_STAGGER = 0.04;
const CARD_INDEX_MAX_STAGGER = 0.22;

export function MonthlyStoriesPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const pageReady = usePageTransitionEntrance();
  const reduced = Boolean(useReducedMotion());
  const [history, setHistory] = useState<StoryHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [selectedItem, setSelectedItem] = useState<StoryHistoryItem | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  /** El botón "Cargar más" se oculta hasta que la última card termina su
   * animación de entrada para que no aparezca antes que el grid. */
  const [loadMoreVisible, setLoadMoreVisible] = useState(false);

  const blockY = storyResourcesEntrance.offsetY;
  const hidden = { opacity: 0, y: blockY };
  const shown = { opacity: 1, y: 0 };
  const blockTransition = (delay: number) =>
    entranceAwareBlockTransition(pageReady, reduced, storyResourcesEntrance.blockDuration, delay);

  /** Las cards de la primera página se animan con stagger; las añadidas con "Cargar más" entran sin retardo extra. */
  const initialLoadCountRef = useRef<number | null>(null);

  const load = useCallback(async () => {
    if (!user?.id) {
      setHistory([]);
      setHasMore(false);
      setOffset(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const monthStart = startOfCurrentMonth().getTime();
      // Sin `passedOnly`: "completada" = cualquier historia terminada (aprobada o
      // no), para que la lista coincida con el contador de Progreso, que cuenta
      // todas las terminadas del mes.
      const result = await usersAPI.getStoryHistory({
        from: monthStart,
        limit: PAGE_SIZE,
        offset: 0,
      });
      setHistory(result.items);
      setHasMore(result.hasMore);
      setOffset(PAGE_SIZE);
      initialLoadCountRef.current = null;
    } catch {
      setHistory([]);
      setHasMore(false);
      setOffset(0);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!loading && history.length > 0 && initialLoadCountRef.current === null) {
      initialLoadCountRef.current = history.length;
    }
  }, [loading, history.length]);

  /** Cada vez que cambia el número de cards (carga inicial o "Cargar más")
   * volvemos a ocultar el botón hasta que la última nueva card termine. */
  useEffect(() => {
    setLoadMoreVisible(false);
  }, [history.length]);

  const handleLoadMore = async () => {
    if (isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    try {
      const monthStart = startOfCurrentMonth().getTime();
      // Sin `passedOnly`: "completada" = cualquier historia terminada (aprobada o
      // no), para que la lista coincida con el contador de Progreso, que cuenta
      // todas las terminadas del mes.
      const result = await usersAPI.getStoryHistory({
        from: monthStart,
        limit: PAGE_SIZE,
        offset,
      });
      setHistory((prev) => [...prev, ...result.items]);
      setHasMore(result.hasMore);
      setOffset((prev) => prev + PAGE_SIZE);
    } catch {
      // Silencioso: si falla, el botón sigue disponible para reintentar.
    } finally {
      setIsLoadingMore(false);
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.page__header}>
        <motion.h1
          className={styles.page__title}
          initial={hidden}
          animate={pageReady ? shown : hidden}
          transition={blockTransition(TITLE_DELAY)}
        >
          {t('monthlyStoriesPage.title')}
        </motion.h1>
        <motion.p
          className={styles.page__subtitle}
          initial={hidden}
          animate={pageReady ? shown : hidden}
          transition={blockTransition(SUBTITLE_DELAY)}
        >
          {t('monthlyStoriesPage.subtitle')}
        </motion.p>
      </header>

      {loading ? (
        <div className={styles.page__feedLoading}>
          <Spinner size="lg" aria-label={t('common.loading')} />
        </div>
      ) : history.length > 0 ? (
        <div className={styles.page__gridBlock}>
          <div className={styles.page__feed}>
            {history.map((item, idx) => {
              const isInitialCard =
                initialLoadCountRef.current === null || idx < initialLoadCountRef.current;
              const baseDelay = isInitialCard
                ? CARD_INITIAL_BASE_DELAY
                : CARD_LOAD_MORE_BASE_DELAY;
              const cardDelay =
                baseDelay + Math.min(idx * CARD_INDEX_STAGGER, CARD_INDEX_MAX_STAGGER);
              const isLast = idx === history.length - 1;
              return (
                <motion.div
                  key={item.id}
                  initial={!reduced ? { opacity: 0, y: 20, scale: 0.95 } : false}
                  whileInView={
                    pageReady && !reduced ? { opacity: 1, y: 0, scale: 1 } : undefined
                  }
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.42, delay: cardDelay, ease: EASE }}
                  onAnimationComplete={isLast ? () => setLoadMoreVisible(true) : undefined}
                >
                  <CompletedStoryCard
                    title={localizedField(item, 'storyTitle', i18n.language)}
                    backgroundImage={item.storyBackgroundImage}
                    onClick={() => {
                      setSelectedItem(item);
                      setDrawerOpen(true);
                    }}
                  />
                </motion.div>
              );
            })}
          </div>
          {hasMore && (
            <motion.div
              key={history.length}
              className={styles.page__loadMore}
              initial={!reduced ? { opacity: 0, y: 16 } : false}
              animate={
                !reduced
                  ? loadMoreVisible
                    ? { opacity: 1, y: 0 }
                    : { opacity: 0, y: 16 }
                  : undefined
              }
              transition={{ duration: 0.38, ease: EASE }}
            >
              <Button variant="primary" onClick={handleLoadMore} disabled={isLoadingMore}>
                {isLoadingMore ? t('common.loading') : t('storiesPage.loadMore')}
              </Button>
            </motion.div>
          )}
        </div>
      ) : (
        <motion.p
          className={styles.page__empty}
          initial={hidden}
          animate={pageReady ? shown : hidden}
          transition={blockTransition(HEADER_BLOCK_DELAY)}
        >
          {t('monthlyStoriesPage.emptyMonth')}
        </motion.p>
      )}

      <CompletedStoryDrawer
        item={selectedItem}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
      />
    </div>
  );
}

export default MonthlyStoriesPage;
